import { ENDPOINTS } from "./MonitoringApiConstants";

const DEBUG = true;

function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

const SAMPLE_MONITORING_ALERTS = [
    {
        id: "11111111-1111-4111-8111-111111111111",
        environment: "local",
        source_id: "espoon_kokoomus",
        status: "open",
        enabled: true,
        fail_streak: 4,
        first_failure_at: "2026-09-16T12:00:00Z",
        last_failure_at: "2026-09-18T12:00:00Z",
        last_success_at: "2026-09-15T12:00:00Z",
        last_error_summary: "403 Client Error",
        last_alerted_at: "2026-09-18T08:00:00Z",
        suppress_until: null,
        suppressed_by: null,
        resolved_at: null,
        failing_for_seconds: 259200,
        timestamp_created: "2026-09-16T13:00:00Z",
        timestamp_updated: "2026-09-18T12:00:00Z",
        alert_type: "fetch_failure",
    },
    {
        id: "22222222-2222-4222-8222-222222222222",
        environment: "local",
        source_id: "staffmax",
        status: "open",
        enabled: false,
        fail_streak: 3,
        first_failure_at: "2026-09-16T08:00:00Z",
        last_failure_at: "2026-09-17T12:00:00Z",
        last_success_at: null,
        last_error_summary: "timeout",
        last_alerted_at: "2026-09-17T12:00:00Z",
        suppress_until: null,
        suppressed_by: null,
        resolved_at: null,
        failing_for_seconds: 172800,
        timestamp_created: "2026-09-16T08:00:00Z",
        timestamp_updated: "2026-09-17T12:00:00Z",
        alert_type: "fetch_failure",
    },
    {
        id: "33333333-3333-4333-8333-333333333333",
        environment: "local",
        source_id: "turun_kaupunki",
        status: "open",
        enabled: true,
        alert_type: "stale_content",
        fail_streak: 45,
        first_failure_at: "2026-09-17T08:00:00Z",
        last_failure_at: "2026-09-21T03:00:00Z",
        last_success_at: "2026-09-17T08:00:00Z",
        last_error_summary: "ei uutta 4 pv · p90: 18 h · kynnys: 1 pv 3 h",
        last_alerted_at: "2026-09-21T03:00:00Z",
        suppress_until: null,
        suppressed_by: null,
        resolved_at: null,
        failing_for_seconds: 345600,
        timestamp_created: "2026-09-21T03:00:00Z",
        timestamp_updated: "2026-09-21T03:00:00Z",
    },
];

const MEDIA_SUMMARY_SAMPLE = [
    {
        id: "helsingin_uutiset",
        name: "Helsingin Uutiset",
        target_media: "HelsinginUutiset",
        sources_count: 42,
        sources_error_count: 2,
        sources_never_run: 5,
    },
    {
        id: "lansivayla",
        name: "Länsiväylä",
        target_media: "Länsiväylä",
        sources_count: 18,
        sources_error_count: 0,
        sources_never_run: 1,
    },
    {
        id: "meksi",
        name: "Meksi",
        target_media: "Meksi",
        sources_count: 70,
        sources_error_count: 3,
        sources_never_run: 12,
    },
];

function buildSampleSources(media) {
    const total = media?.sources_count ?? 0;
    const errors = media?.sources_error_count ?? 0;
    const neverRun = media?.sources_never_run ?? 0;

    const sources = [];
    for (let i = 0; i < total; i += 1) {
        let status = "healthy";
        if (i < errors) {
            status = "error";
        } else if (i < errors + neverRun) {
            status = "never_run";
        }

        const sourceId = `${media.id}_source_${i + 1}`;
        sources.push({
            source_id: sourceId,
            status,
            enabled: i !== 0,
            error_summary: status === "error" ? "Esimerkkivirhe haun aikana" : null,
            gap_stats:
                status === "never_run"
                    ? null
                    : {
                          last_new_at: new Date(
                              Date.now() - (i + 1) * 36 * 60 * 60 * 1000
                          ).toISOString(),
                          gap_count: 24 + i,
                          p90_gap_seconds: 18 * 3600,
                          p99_gap_seconds: 2 * 86400,
                          stats_computed_at: "2026-09-21T02:00:00Z",
                          current_silence_seconds: ((i + 1) * 24 + 6) * 3600,
                      },
            weekly_summary:
                status === "never_run"
                    ? null
                    : {
                          source_id: sourceId,
                          source_type: "rss",
                          week_start_date: "2026-06-22",
                          week_end_date: "2026-06-28",
                          runs_total: 7,
                          runs_completed: status === "error" ? 5 : 7,
                          runs_failed: status === "error" ? 2 : 0,
                          fetch_failures: status === "error" ? 2 : 0,
                          items_found: 24,
                          items_new: 6,
                          items_skipped: 18,
                          items_failed: status === "error" ? 1 : 0,
                          last_run_at: "2026-06-28T06:00:00Z",
                          last_run_status: status === "error" ? "failed" : "completed",
                          last_run_trigger: "timer",
                          last_error_summary:
                              status === "error" ? "Esimerkkivirhe haun aikana" : null,
                          pipeline_enqueued: 6,
                          pipeline_processing: 0,
                          pipeline_evaluator_passed: 4,
                          pipeline_evaluator_rejected: 2,
                          pipeline_evaluation_skipped: 0,
                          pipeline_writing: 0,
                          pipeline_reviewing: 0,
                          pipeline_date_validating: 0,
                          pipeline_publish_attempted: 4,
                          pipeline_publish_succeeded: 4,
                          pipeline_publish_failed: 0,
                          pipeline_editorial_published: 0,
                          pipeline_published: 4,
                          pipeline_failed: 0,
                      },
        });
    }
    return sources;
}

function buildSampleSourceRuns(sourceId, days = 14) {
    const now = Date.now();
    const periodEnd = new Date(now).toISOString();
    const periodStart = new Date(now - days * 24 * 60 * 60 * 1000).toISOString();
    const runs = [];

    for (let i = 0; i < 30; i += 1) {
        const startedAt = new Date(now - i * 12 * 60 * 60 * 1000);
        const finishedAt = new Date(startedAt.getTime() + 50 * 1000);
        const failed = i === 2;
        const itemsNew = i % 3 === 0 ? 1 : 0;

        runs.push({
            id: `00000000-0000-4000-8000-${String(i).padStart(12, "0")}`,
            source_id: sourceId,
            source_type: "scraping",
            run_started_at: startedAt.toISOString(),
            run_finished_at: finishedAt.toISOString(),
            status: failed ? "failed" : "completed",
            able_to_fetch_data: !failed,
            items_found: itemsNew,
            items_new: itemsNew,
            items_skipped: 0,
            items_failed: failed ? 1 : 0,
            error_summary: failed ? "Esimerkkivirhe haun aikana" : null,
            environment: "local",
            trigger: i % 2 === 0 ? "timer" : "manual",
            metadata: { source_group: "news" },
            timestamp_created: startedAt.toISOString(),
            timestamp_updated: finishedAt.toISOString(),
        });
    }

    return {
        source_id: sourceId,
        enabled: !String(sourceId).endsWith('_source_1'),
        period_start_at: periodStart,
        period_end_at: periodEnd,
        runs,
    };
}

function buildSamplePipelineItems(runId) {
    const now = Date.now();
    const baseTime = now - 60 * 60 * 1000;

    const makeEvents = (itemId, steps) =>
        steps.map((step, index) => ({
            id: `10000000-0000-4000-8000-${itemId}${String(index).padStart(11, "0")}`,
            pipeline_item_id: `20000000-0000-4000-8000-${itemId}000000000000`,
            event_type: step.event_type,
            stage: step.stage ?? null,
            status_after: step.status_after,
            payload: step.payload ?? null,
            occurred_at: new Date(baseTime + index * 60 * 1000).toISOString(),
            timestamp_created: new Date(baseTime + index * 60 * 1000).toISOString(),
            timestamp_updated: new Date(baseTime + index * 60 * 1000).toISOString(),
        }));

    return [
        {
            item: {
                id: `20000000-0000-4000-8000-010000000000`,
                source_ingestion_run_id: runId,
                source_id: "example_source",
                source_type: "scraping",
                target_media: "ExampleMedia",
                category: "news",
                content_key: "article-1",
                content_title: "Esimerkkiuutinen 1",
                content_url: "https://example.com/article-1",
                skip_evaluation: false,
                status: "published",
                evaluator_decision: "relevant",
                evaluator_explanation: null,
                news_id: "30000000-0000-4000-8000-010000000000",
                error_text: null,
                first_seen_at: new Date(baseTime).toISOString(),
                last_event_at: new Date(baseTime + 5 * 60 * 1000).toISOString(),
                queue_enqueued_at: new Date(baseTime).toISOString(),
                processing_started_at: new Date(baseTime + 60 * 1000).toISOString(),
                evaluator_finished_at: new Date(baseTime + 2 * 60 * 1000).toISOString(),
                published_at: new Date(baseTime + 5 * 60 * 1000).toISOString(),
                timestamp_created: new Date(baseTime).toISOString(),
                timestamp_updated: new Date(baseTime + 5 * 60 * 1000).toISOString(),
            },
            events: makeEvents("01", [
                { event_type: "enqueued", status_after: "queued", stage: "queue" },
                { event_type: "dequeue_started", status_after: "processing", stage: "processing" },
                { event_type: "evaluator_completed", status_after: "evaluator_passed", stage: "evaluator" },
                { event_type: "writer_completed", status_after: "writing", stage: "writer" },
                { event_type: "publish_succeeded", status_after: "published", stage: "publish" },
            ]),
        },
        {
            item: {
                id: `20000000-0000-4000-8000-020000000000`,
                source_ingestion_run_id: runId,
                source_id: "example_source",
                source_type: "scraping",
                target_media: "ExampleMedia",
                category: "news",
                content_key: "article-2",
                content_title: "Esimerkkiuutinen 2",
                content_url: null,
                skip_evaluation: false,
                status: "evaluator_rejected",
                evaluator_decision: "not_relevant",
                evaluator_explanation: "Aihe ei relevantti",
                news_id: null,
                error_text: null,
                first_seen_at: new Date(baseTime + 10 * 60 * 1000).toISOString(),
                last_event_at: new Date(baseTime + 12 * 60 * 1000).toISOString(),
                queue_enqueued_at: new Date(baseTime + 10 * 60 * 1000).toISOString(),
                processing_started_at: new Date(baseTime + 11 * 60 * 1000).toISOString(),
                evaluator_finished_at: new Date(baseTime + 12 * 60 * 1000).toISOString(),
                published_at: null,
                timestamp_created: new Date(baseTime + 10 * 60 * 1000).toISOString(),
                timestamp_updated: new Date(baseTime + 12 * 60 * 1000).toISOString(),
            },
            events: makeEvents("02", [
                { event_type: "enqueued", status_after: "queued", stage: "queue" },
                { event_type: "dequeue_started", status_after: "processing", stage: "processing" },
                { event_type: "evaluator_completed", status_after: "evaluator_rejected", stage: "evaluator" },
            ]),
        },
        {
            item: {
                id: `20000000-0000-4000-8000-030000000000`,
                source_ingestion_run_id: runId,
                source_id: "example_rss",
                source_type: "rss",
                target_media: "ExampleMedia",
                category: "urheilu",
                content_key: "article-3",
                content_title: "Duplikaatti estetty",
                content_url: "https://example.com/article-3",
                skip_evaluation: false,
                status: "duplicate_rejected",
                evaluator_decision: null,
                evaluator_explanation:
                    "near_duplicate; matched=veikkausliiga; score=0.910",
                news_id: null,
                error_text: null,
                first_seen_at: new Date(baseTime + 20 * 60 * 1000).toISOString(),
                last_event_at: new Date(baseTime + 22 * 60 * 1000).toISOString(),
                queue_enqueued_at: new Date(baseTime + 20 * 60 * 1000).toISOString(),
                processing_started_at: new Date(baseTime + 21 * 60 * 1000).toISOString(),
                evaluator_finished_at: null,
                published_at: null,
                timestamp_created: new Date(baseTime + 20 * 60 * 1000).toISOString(),
                timestamp_updated: new Date(baseTime + 22 * 60 * 1000).toISOString(),
            },
            events: makeEvents("03", [
                { event_type: "enqueued", status_after: "queued", stage: "queue" },
                { event_type: "dequeue_started", status_after: "processing", stage: "processing" },
                {
                    event_type: "duplicate_check_completed",
                    status_after: "duplicate_rejected",
                    stage: "duplicate_check",
                    payload: {
                        decision: "reject",
                        reason: "near_duplicate",
                        confidence: 0.94,
                        score: 0.91,
                        mode: "enforce",
                        enforced: true,
                    },
                },
            ]),
        },
    ];
}

export async function localFetch({ endpoint, fetchUrl, fetchParameters, parameters }) {
    if (DEBUG) console.log(`localMonitoringFetch :: ${fetchParameters.method} :: ${fetchUrl}`);

    await delay(250);

    switch (endpoint) {
        case ENDPOINTS.getMediaSummary:
            return new Response(JSON.stringify(MEDIA_SUMMARY_SAMPLE), {
                status: 200,
                headers: { "Content-Type": "application/json" },
            });

        case ENDPOINTS.getAllSourcesSummary: {
            const sources = MEDIA_SUMMARY_SAMPLE.flatMap((media) =>
                buildSampleSources(media)
            );
            return new Response(JSON.stringify({ sources }), {
                status: 200,
                headers: { "Content-Type": "application/json" },
            });
        }

        case ENDPOINTS.getMonitoringAlerts:
            return new Response(
                JSON.stringify({ alerts: SAMPLE_MONITORING_ALERTS }),
                {
                    status: 200,
                    headers: { "Content-Type": "application/json" },
                }
            );

        case ENDPOINTS.suppressMonitoringAlert:
        case ENDPOINTS.clearMonitoringAlertSuppression: {
            const alertId = parameters?.alert_id ?? parameters?.alertId;
            const alert = SAMPLE_MONITORING_ALERTS.find((item) => item.id === alertId);

            if (!alert) {
                return new Response(
                    JSON.stringify({ message: "Open monitoring alert not found" }),
                    { status: 404, headers: { "Content-Type": "application/json" } }
                );
            }

            if (endpoint === ENDPOINTS.clearMonitoringAlertSuppression) {
                const until = alert.suppress_until
                    ? new Date(alert.suppress_until).getTime()
                    : 0;
                if (!until || until <= Date.now()) {
                    return new Response(
                        JSON.stringify({ message: "Monitoring alert is not currently suppressed" }),
                        { status: 400, headers: { "Content-Type": "application/json" } }
                    );
                }
                alert.suppress_until = null;
                alert.suppressed_by = null;
            } else {
                const body = fetchParameters?.body
                    ? JSON.parse(fetchParameters.body)
                    : {};
                const days = Number.parseInt(body?.days, 10);
                if (!Number.isFinite(days) || days < 1) {
                    return new Response(
                        JSON.stringify({ message: "Suppress duration must be at least one day" }),
                        { status: 400, headers: { "Content-Type": "application/json" } }
                    );
                }
                const suppressUntil = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
                alert.suppress_until = suppressUntil.toISOString();
                alert.suppressed_by = "Local Dev";
            }

            alert.timestamp_updated = new Date().toISOString();

            return new Response(JSON.stringify(alert), {
                status: 200,
                headers: { "Content-Type": "application/json" },
            });
        }

        case ENDPOINTS.getMediaSourcesSummary: {
            const mediaId = parameters?.media_id ?? parameters?.mediaId ?? parameters?.id;
            const media = MEDIA_SUMMARY_SAMPLE.find((m) => String(m.id) === String(mediaId));

            if (!media) {
                return new Response(
                    JSON.stringify({ message: "Media not found" }),
                    { status: 404, headers: { "Content-Type": "application/json" } }
                );
            }

            const payload = {
                id: media.id,
                name: media.name,
                target_media: media.target_media,
                sources: buildSampleSources(media),
            };
            return new Response(JSON.stringify(payload), {
                status: 200,
                headers: { "Content-Type": "application/json" },
            });
        }

        case ENDPOINTS.getSourceRuns: {
            const sourceId = parameters?.source_id ?? parameters?.sourceId;
            if (!sourceId) {
                return new Response(
                    JSON.stringify({ message: "Source not found" }),
                    { status: 404, headers: { "Content-Type": "application/json" } }
                );
            }
            const days = Number(parameters?.days ?? 14);
            return new Response(JSON.stringify(buildSampleSourceRuns(sourceId, days)), {
                status: 200,
                headers: { "Content-Type": "application/json" },
            });
        }

        case ENDPOINTS.getRunPipelineItems: {
            const runId = parameters?.run_id ?? parameters?.runId;
            if (!runId) {
                return new Response(
                    JSON.stringify({ message: "Run not found" }),
                    { status: 404, headers: { "Content-Type": "application/json" } }
                );
            }
            return new Response(JSON.stringify(buildSamplePipelineItems(runId)), {
                status: 200,
                headers: { "Content-Type": "application/json" },
            });
        }

        default:
            throw new Error(`localMonitoringFetch :: Unknown endpoint: ${endpoint}`);
    }
}
